import React, { useMemo } from 'react'
import { IonCol, IonGrid, IonLabel, IonRow } from '@ionic/react'
import { IBodySurveyGroup, IQaChartSummaryPage, IQaChartSummarySection } from '../../store/qaAddendum.types'
import ChartSummaryElements from './ChartSummaryElements.content'
import ChartSummaryEventHeader from './ChartSummaryEventHeader.content'
import ChartSummaryRowHeader from './ChartSummaryRowHeader.content'
import ChartSummaryList, { IChartSummaryListItem } from './ChartSummaryList.content'
import {
  getBodySurveyGroups,
  getEventEntries,
  hasRenderableContent,
  isBodySurveySection,
} from '../../../utils/qaAddendumChartSummary.util'
import './ChartSummaryPageDetail.content.css'

const TIMELINE_PAGE = 'Timeline'
const ADDITIONAL_ASSESSMENTS_PAGE = 'Additional Assessments'

interface IChartSummaryPageDetailContentProps {
  page: IQaChartSummaryPage
}

const renderBodyPartRow = (label: string, value: string, key: string) =>
  value ? (
    <IonRow key={key} className='qa-addendum-detail-row qa-addendum-chart-summary-inline-row'>
      <IonCol size='12'>
        <IonLabel className='qa-addendum-detail-row-key fw-700 font-size-16'>{label}: </IonLabel>
        <IonLabel className='qa-addendum-detail-row-value fw-400 font-size-16'>{value}</IonLabel>
      </IonCol>
    </IonRow>
  ) : null

const renderBodySurveyGroup = (group: IBodySurveyGroup) => (
  <div className='qa-addendum-body-survey'>
    {group.categories.map((category) => (
      <div key={`${group.key}-${category.key}`}>
        <div className='qa-addendum-chart-summary-section-heading'>
          <IonLabel className='grid-item-divider-label' color='medium'>
            {category.name}
          </IonLabel>
        </div>
        <div className='qa-addendum-chart-summary-section-card'>
          {category.parts.map((part) => {
            const showName = Boolean(part.name) && part.name !== category.name
            return (
              <div key={`${group.key}-${part.key}`} className='qa-addendum-body-survey-part'>
                {showName ? (
                  <div className='qa-addendum-chart-summary-container-header'>
                    <IonLabel className='qa-addendum-detail-row-key fw-700 font-size-16'>{part.name}: </IonLabel>
                  </div>
                ) : null}
                <div className={showName ? 'qa-addendum-chart-summary-record-fields' : undefined}>
                  <IonGrid className='ion-no-padding'>
                    {renderBodyPartRow('Present', part.present, `${part.key}-present`)}
                    {renderBodyPartRow('Not Present', part.notDocumented, `${part.key}-neg`)}
                    {renderBodyPartRow('Comment', part.comment, `${part.key}-comment`)}
                  </IonGrid>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    ))}
  </div>
)

const toBodySurveyItems = (groups: IBodySurveyGroup[]): IChartSummaryListItem[] =>
  groups.map((group) => ({
    key: group.key,
    testId: `QaBodySurvey-${group.key}`,
    renderHeader: () => <ChartSummaryRowHeader title={group.title} time={group.time} date={group.date} />,
    renderContent: () => renderBodySurveyGroup(group),
  }))

const toSectionItems = (page: IQaChartSummaryPage): IChartSummaryListItem[] =>
  (page.sections ?? []).flatMap((section, index) => {
    const key = `section-${section.sectionId}-${index}`
    if (isBodySurveySection(section)) {
      const bodySurveyGroups = getBodySurveyGroups(section)
      return bodySurveyGroups ? toBodySurveyItems(bodySurveyGroups) : []
    }

    if (!hasRenderableContent(section.elements)) return []

    return [
      {
        key,
        title: section.header?.trim() || `Section ${section.sectionId}`,
        testId: `QaChartSummarySectionToggle-${section.sectionId}`,
        renderContent: () => (
          <ChartSummaryElements elements={section.elements} keyPrefix={key} sectionHeader={section.header} />
        ),
      },
    ]
  })

const toTimelineItems = (page: IQaChartSummaryPage): IChartSummaryListItem[] =>
  getEventEntries(page).map((entry) => ({
    key: entry.key,
    testId: `QaChartSummaryEventRecord-${entry.key}`,
    renderHeader: () => <ChartSummaryEventHeader entry={entry} />,
    renderContent: () => <ChartSummaryElements elements={entry.record.elements} keyPrefix={entry.key} />,
  }))

const PAGE_ITEM_BUILDERS: Record<string, (page: IQaChartSummaryPage) => IChartSummaryListItem[]> = {
  [TIMELINE_PAGE]: toTimelineItems,
  [ADDITIONAL_ASSESSMENTS_PAGE]: toSectionItems,
}

const ChartSummaryPageDetailContent: React.FC<IChartSummaryPageDetailContentProps> = ({ page }) => {
  const pageName = page.pageName?.trim() ?? ''
  const layoutItems = useMemo(() => {
    const buildItems = PAGE_ITEM_BUILDERS[pageName]
    return buildItems ? buildItems(page) : null
  }, [page, pageName])

  if (layoutItems) {
    return (
      <div className='qa-addendum-chart-summary-detail' data-testid={`QaChartSummaryPageDetail-${page.pageId}`}>
        <ChartSummaryList items={layoutItems} />
      </div>
    )
  }

  const renderSection = (section: IQaChartSummarySection, index: number) => {
    if (!hasRenderableContent(section.elements)) return null

    const keyPrefix = `section-${section.sectionId}-${index}`

    return (
      <div key={keyPrefix} data-testid={`QaChartSummarySection-${section.sectionId}`}>
        {section.header ? (
          <div className='qa-addendum-chart-summary-section-heading'>
            <IonLabel className='grid-item-divider-label' color='medium'>
              {section.header}
            </IonLabel>
          </div>
        ) : null}
        <div className='qa-addendum-chart-summary-section-card'>
          <ChartSummaryElements elements={section.elements} keyPrefix={keyPrefix} sectionHeader={section.header} />
        </div>
      </div>
    )
  }

  return (
    <div className='qa-addendum-chart-summary-detail' data-testid={`QaChartSummaryPageDetail-${page.pageId}`}>
      {(page.sections ?? []).map(renderSection)}
    </div>
  )
}

export default ChartSummaryPageDetailContent
